import { useEffect, useState } from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import {
  Bold, Italic, Strikethrough, Heading2, Heading3, List, ListOrdered, Quote, Link2, Undo2, Redo2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  className?: string;
}

interface ToolButton {
  icon: typeof Bold;
  label: string;
  run: (e: Editor) => void;
  active?: (e: Editor) => boolean;
}

const TOOLS: ToolButton[] = [
  { icon: Bold, label: 'Bold', run: (e) => e.chain().focus().toggleBold().run(), active: (e) => e.isActive('bold') },
  { icon: Italic, label: 'Italic', run: (e) => e.chain().focus().toggleItalic().run(), active: (e) => e.isActive('italic') },
  { icon: Strikethrough, label: 'Strikethrough', run: (e) => e.chain().focus().toggleStrike().run(), active: (e) => e.isActive('strike') },
  { icon: Heading2, label: 'Heading 2', run: (e) => e.chain().focus().toggleHeading({ level: 2 }).run(), active: (e) => e.isActive('heading', { level: 2 }) },
  { icon: Heading3, label: 'Heading 3', run: (e) => e.chain().focus().toggleHeading({ level: 3 }).run(), active: (e) => e.isActive('heading', { level: 3 }) },
  { icon: List, label: 'Bullet list', run: (e) => e.chain().focus().toggleBulletList().run(), active: (e) => e.isActive('bulletList') },
  { icon: ListOrdered, label: 'Numbered list', run: (e) => e.chain().focus().toggleOrderedList().run(), active: (e) => e.isActive('orderedList') },
  { icon: Quote, label: 'Quote', run: (e) => e.chain().focus().toggleBlockquote().run(), active: (e) => e.isActive('blockquote') },
];

export function RichTextEditor({ value, onChange, placeholder, className }: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: 'noopener noreferrer nofollow', target: '_blank' } }),
    ],
    content: value,
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
    editorProps: {
      attributes: {
        class: 'prose prose-sm dark:prose-invert max-w-none min-h-[200px] px-3 py-2 focus:outline-none',
      },
    },
  });

  // Sync external value changes (e.g. opening an edit modal) without clobbering the cursor.
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [value, editor]);

  const [linkDialog, setLinkDialog] = useState<{ open: boolean; value: string }>({ open: false, value: '' });

  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes('link').href as string | undefined;
    setLinkDialog({ open: true, value: prev ?? 'https://' });
  };

  const applyLink = (url: string) => {
    setLinkDialog({ open: false, value: '' });
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  };

  return (
    <div className={cn('rounded-md border border-input bg-background', className)}>
      <div className="flex flex-wrap items-center gap-0.5 border-b border-border/60 p-1">
        {TOOLS.map((t) => (
          <button
            key={t.label}
            type="button"
            title={t.label}
            aria-label={t.label}
            onClick={() => t.run(editor)}
            className={cn(
              'rounded p-1.5 text-foreground/70 transition-colors hover:bg-muted hover:text-foreground',
              t.active?.(editor) && 'bg-primary/10 text-primary',
            )}
          >
            <t.icon className="h-4 w-4" />
          </button>
        ))}
        <button
          type="button"
          title="Link"
          aria-label="Link"
          onClick={setLink}
          className={cn(
            'rounded p-1.5 text-foreground/70 transition-colors hover:bg-muted hover:text-foreground',
            editor.isActive('link') && 'bg-primary/10 text-primary',
          )}
        >
          <Link2 className="h-4 w-4" />
        </button>
        <span className="mx-1 h-5 w-px bg-border" />
        <button type="button" title="Undo" aria-label="Undo" onClick={() => editor.chain().focus().undo().run()} className="rounded p-1.5 text-foreground/70 hover:bg-muted hover:text-foreground">
          <Undo2 className="h-4 w-4" />
        </button>
        <button type="button" title="Redo" aria-label="Redo" onClick={() => editor.chain().focus().redo().run()} className="rounded p-1.5 text-foreground/70 hover:bg-muted hover:text-foreground">
          <Redo2 className="h-4 w-4" />
        </button>
      </div>
      <EditorContent editor={editor} placeholder={placeholder} />

      <Dialog open={linkDialog.open} onOpenChange={(o) => { if (!o) setLinkDialog({ open: false, value: '' }); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Insert Link</DialogTitle>
          </DialogHeader>
          <Input
            value={linkDialog.value}
            onChange={(e) => setLinkDialog((d) => ({ ...d, value: e.target.value }))}
            placeholder="https://"
            onKeyDown={(e) => { if (e.key === 'Enter') applyLink(linkDialog.value); }}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDialog({ open: false, value: '' })}>Cancel</Button>
            <Button onClick={() => applyLink(linkDialog.value)}>Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
